import clsx from 'clsx';
import { useMemo } from 'react';

import { ClientOnly } from '@/components/ClientOnly';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import type { ResultsFilters } from './FilterTypes';
import { useRefetchOnGrade } from './LiveStatsRefresh';
import { resolveSportIds } from './consts';

export function ResultsChart({
  userId,
  filters,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  // Was destructured but never declared — every call site already passes it.
  filters: ResultsFilters;
}) {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase, startDate, endDate, playType, free, gameType } = filters;

  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);

  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  const getChartRequest = useMemo(() => {
    const req: RequestSchema<
      API.MembershipProductStats & { chart: { date: string; units: string | number }[] } & {
        playType?: Record<API.Results, API.MembershipProductStats>;
      } & {
        period?: Record<API.Results, API.MembershipProductStats>;
      } & {
        gameType?: Record<API.Results, API.MembershipProductStats>;
      }
    > = {
      name: 'getChartRequest',
      input: `/api/v1/messageReceipt/stats/${userId}`,
    };

    return req;
  }, [userId]);

  const chartRequest = useRequest(
    getChartRequest,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        suspendOnClient: false,
        clientOnly: !isArchive, // on /result (my results) we can't fetch this endpoint in SSR as it needs cookies
        query: {
          chart: true,
          count: true,
          tz:
            (typeof Intl !== 'undefined' &&
              Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone) ||
            undefined,
          playType:
            playType === 'full'
              ? ['side', 'total', 'prop', 'future', 'not-available']
              : playType === 'hp'
                ? ['side', 'total', 'not-available']
                : playType === 'st'
                  ? ['side', 'total']
                  : playType === 'pf'
                    ? ['prop', 'future']
                    : playType && typeof playType === 'string'
                      ? [playType]
                      : playType || ['side', 'total', 'prop', 'future', 'not-available'],
          [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
          'message.serviceIds': serviceIds,
          startDate,
          endDate,
          free,
          gameType,
          groupBy: isArchive ? 'playType' : undefined,
        },
      }),
      [isArchive, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  useRefetchOnGrade([{ request: chartRequest, active: true }]);

  return (
    <ClientOnly
      component={() => import('./AreaChart')}
      request={chartRequest}
      isArchive={isArchive}
    >
      <ResultsChartLoading />
    </ClientOnly>
  );
}

/**
 * Placeholder that occupies EXACTLY the rendered chart's box (h-36 / sm:h-56) at every
 * width. It previously used a taller height than the chart it stands in for and was
 * `hidden` below `sm`, so each load shifted the card by ~64px on desktop and collapsed
 * the slot entirely on mobile — the blips when moving between sports (#2004).
 */
export function ResultsChartLoading() {
  return (
    <div
      className={clsx(
        'mt-4 h-36 w-full min-w-0 rounded-lg sm:h-56',
        'animate-pulse bg-zinc-100 dark:bg-white/[0.04]',
      )}
      aria-hidden
    />
  );
}
